import { classes } from "./classes.js";
import { BANK_FS_META, BANK_RADIUS_PILL } from "./tokens.js";
import type { MascotState } from "./HakiliMascot.js";

/**
 * `F-0c`: the word that says what Hakili is doing (prototype `.schip`): a dot and the state's name.
 *
 * The WORD is the caller's (the catalogue); the dot's colour is the second signal, never the only one. `bay`
 * sits on the dark bay; `light` is for a page with no bay behind it (empty and error states).
 */
export interface StateBadgeProps {
  readonly state: MascotState;
  readonly label: string;
  readonly variant?: "bay" | "light";
  readonly className?: string;
}

export function StateBadge({ state, label, variant = "bay", className }: StateBadgeProps): React.JSX.Element {
  return (
    <span
      role="status"
      data-kit="state-badge"
      data-state={state}
      data-variant={variant}
      className={classes(
        "inline-flex items-center gap-2 px-3 py-1.5 font-medium whitespace-nowrap",
        BANK_RADIUS_PILL,
        BANK_FS_META,
        className,
      )}
    >
      <i aria-hidden="true" className="inline-block h-2 w-2 rounded-full" />
      {label}
    </span>
  );
}

/**
 * `F-0c`: **the shape beside the face** (prototype `.mbadge`): one outline per state, so the state is a shape and a
 * glyph and not a colour alone (a circle with lines for reading, a seal with a tick for done, a triangle with a mark
 * for flagging, a broken ring for failed; `idle` draws none). Decorative (`aria-hidden`): the word is `StateBadge`.
 */
export function MascotBadge({ state }: { readonly state: MascotState }): React.JSX.Element | null {
  const common = {
    viewBox: "0 0 40 40",
    "aria-hidden": "true" as const,
    "data-kit": "mascot-badge",
    "data-state": state,
    className: "block h-full w-full",
  };
  if (state === "reading") {
    return (
      <svg {...common}>
        <circle
          cx="20"
          cy="20"
          r="19"
          strokeWidth="2"
          className="fill-[color:var(--hk-brand)] stroke-white"
        />
        <rect className="l1 fill-white" x="10" y="12" width="20" height="3.6" rx="1.8" />
        <rect className="l2 fill-white" x="10" y="18.2" width="14" height="3.6" rx="1.8" />
        <rect className="l3 fill-white" x="10" y="24.4" width="17" height="3.6" rx="1.8" />
      </svg>
    );
  }
  if (state === "done") {
    return (
      <svg {...common}>
        <path
          d="M20 1 L23.65 4.01 L28.24 2.88 L30.23 7.18 L34.85 8.15 L34.78 12.88 L38.52 15.77 L36.4 20 L38.52 24.23 L34.78 27.12 L34.85 31.85 L30.23 32.82 L28.24 37.12 L23.65 35.99 L20 39 L16.35 35.99 L11.76 37.12 L9.77 32.82 L5.15 31.85 L5.22 27.12 L1.48 24.23 L3.6 20 L1.48 15.77 L5.22 12.88 L5.15 8.15 L9.77 7.18 L11.76 2.88 L16.35 4.01 Z"
          strokeWidth="1.6"
          strokeLinejoin="round"
          className="fill-[color:var(--hk-ok)] stroke-white"
        />
        {/* D2-design §5: « a seal bearing the Hakili mark (never a check: it proposes, it does not approve) ». The mark, as D2 draws it. */}
        <g transform="translate(10.2 10.2) scale(.215)" className="fill-white">
          <rect x="18" y="14" width="15" height="68" rx="7.5" />
          <rect x="63" y="14" width="15" height="68" rx="7.5" />
          <rect x="18" y="40.5" width="60" height="15" rx="7.5" />
        </g>
      </svg>
    );
  }
  if (state === "flagging") {
    return (
      <svg {...common}>
        <path
          d="M20 3 L38 35 H2 Z"
          strokeWidth="2"
          strokeLinejoin="round"
          className="fill-[color:var(--hk-bay-warn)] stroke-white"
        />
        <rect x="18.2" y="13.5" width="3.6" height="11" rx="1.8" className="fill-[color:var(--hk-warn)]" />
        <circle cx="20" cy="29.2" r="2.2" className="fill-[color:var(--hk-warn)]" />
      </svg>
    );
  }
  if (state === "failed") {
    return (
      <svg {...common}>
        <circle
          cx="20"
          cy="20"
          r="19"
          strokeWidth="2"
          className="fill-[color:var(--hk-bay-muted)] stroke-white"
        />
        <circle
          cx="20"
          cy="20"
          r="9"
          fill="none"
          strokeWidth="3.4"
          strokeDasharray="38 18"
          strokeLinecap="round"
          transform="rotate(-50 20 20)"
          className="stroke-white"
        />
        <rect
          x="26"
          y="11"
          width="9"
          height="3.4"
          rx="1.7"
          transform="rotate(40 30 13)"
          className="fill-white"
        />
      </svg>
    );
  }
  return null;
}
